// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import AppLayout from './AppLayout';

function CheckpointSelector({ onSelect, onCancel }) {
  const navigate = useNavigate();
  const [cellSearch, setCellSearch] = useState('');

  // Carrega células do usuário
  const { data: cellsData, isLoading: cellsLoading } = useQuery({
    queryKey: ['cells'],
    queryFn: async () => {
      const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
      const url = isCheckpointContext ? '/cells?only_led_by_me=true' : '/cells';
      const response = await api.get(url);
      return response.data.data || [];
    },
  });

  const cells = cellsData || [];

  // Filtra células por busca
  const filteredCells = cells.filter(cell => {
    if (!cellSearch) return true;
    const searchLower = cellSearch.toLowerCase();
    return (
      cell.name?.toLowerCase().includes(searchLower) ||
      cell.address?.toLowerCase().includes(searchLower) ||
      cell.neighborhood?.toLowerCase().includes(searchLower)
    );
  });

  // Formata dia da semana
  const getDayOfWeekLabel = (day) => {
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return day !== null && day >= 0 && day <= 6 ? days[day] : '-';
  };

  // Formata hora
  const formatTime = (time) => {
    if (!time) return '';
    if (time.match(/^\d{2}:\d{2}$/)) return time;
    return time.substring(0, 5); // HH:MM
  };

  // Handler de seleção de célula
  const handleCellSelect = (cell) => {
    if (onSelect) {
      onSelect(cell.id, null);
    } else {
      const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
      if (isCheckpointContext) {
        navigate(`/?cell_id=${cell.id}`);
      } else {
        navigate(`/checkpoint?cell_id=${cell.id}`);
      }
    }
  };

  // Handler de voltar
  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
      if (!isCheckpointContext) {
        navigate('/dashboard');
      }
    }
  };

  if (cellsLoading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="checkpoint-selector" style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            className="btn btn-text"
            onClick={handleBack}
            style={{ marginBottom: '1rem' }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Voltar
          </button>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Iniciar Checkpoint
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Selecione a célula para o checkpoint
          </p>
        </div>

        <div>
          {/* Busca */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar célula por nome, endereço ou bairro..."
              value={cellSearch}
              onChange={(e) => setCellSearch(e.target.value)}
            />
          </div>

          {/* Lista de Células */}
          {filteredCells.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                groups
              </span>
              <h3 style={{ marginBottom: '0.5rem' }}>Nenhuma célula encontrada</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {cellSearch 
                  ? 'Tente ajustar sua busca'
                  : 'Você não tem acesso a nenhuma célula no momento'
                }
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {filteredCells.map(cell => (
                <div
                  key={cell.id}
                  className="card"
                  style={{
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: '1px solid var(--border)'
                  }}
                  onClick={() => handleCellSelect(cell)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  }}
                >
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {cell.name}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {cell.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>location_on</span>
                        <span>{cell.address}</span>
                      </div>
                    )}
                    {(cell.day_of_week !== null || cell.start_time) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>schedule</span>
                        <span>
                          {getDayOfWeekLabel(cell.day_of_week)} {cell.start_time && `às ${formatTime(cell.start_time)}`}
                        </span>
                      </div>
                    )}
                    {cell.current_members !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>people</span>
                        <span>{cell.current_members} {cell.current_members === 1 ? 'membro' : 'membros'}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default CheckpointSelector;
